/**
 * Playback-register folding.
 *
 * Some instruments have a physical range narrower than the pitches an exercise
 * may generate (guitar timbre is used for melody/interval/chord dictation whose
 * register isn't bound to the fretboard). Rather than pitch-shift a sample past
 * the instrument's real range — which sounds synthetic and, on Android, triggers
 * ExoPlayer's Sonic OLA "ghost note" transient on any rate ≠ 1.0 playback — we
 * fold out-of-range pitches by whole octaves back into the instrument's sampled
 * range. Pitch class is preserved; only the octave changes.
 *
 * This affects audio only. Notation renders the true pitch.
 */

import type { NoteEvent } from "./audio-plan.js";

export interface MidiRange {
  /** Lowest playable MIDI note, inclusive. */
  min: number;
  /** Highest playable MIDI note, inclusive. */
  max: number;
}

/**
 * Per-instrument playback range. Only instruments whose musical use exceeds
 * their sampled range need an entry; anything absent plays every note as-is.
 *
 * Guitar: C2–D6 (MIDI 36–86). D6 is the top note of a 22-fret guitar's high-E
 * string; everything above is above the instrument. Matches the bundled sample
 * sets (apps/mobile/assets/guitar-samples, apps/web public/samples/guitar).
 */
export const INSTRUMENT_PLAYBACK_RANGE: Record<string, MidiRange> = {
  guitar: { min: 36, max: 86 },
};

/** Fold a MIDI note by whole octaves until it sits within [min, max]. */
export function foldMidiIntoRange(midi: number, range: MidiRange): number {
  let m = midi;
  while (m > range.max) m -= 12;
  while (m < range.min) m += 12;
  return m;
}

/**
 * Return `events` with every note folded into the instrument's playback range.
 * Instruments with no range restriction are returned unchanged (same reference),
 * and in-range events keep their identity so callers can detect a no-op cheaply.
 */
export function foldEventsForInstrument(events: NoteEvent[], instrument: string): NoteEvent[] {
  const range = INSTRUMENT_PLAYBACK_RANGE[instrument];
  if (!range) return events;
  return events.map((e) => {
    const midi = foldMidiIntoRange(e.midi, range);
    return midi === e.midi ? e : { ...e, midi };
  });
}
